/* eslint-disable camelcase */

import { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useLocalization } from "@fluent/react";

import { Tabs } from "@narmi/design_system";
import { featureEquals } from "byzantine/src/Feature";
import type Account from "byzantine/src/Account";
import type Membership from "byzantine/src/Membership";
import { type Features, type FeatureValue } from "byzantine/src/Feature";
import { entities } from "byzantine";
import { SudoProvider, SudoRoutes } from "../SudoContext";
import RequestOTP from "../SudoContext/RequestOTP";
import SubmitOTP from "../SudoContext/SubmitOTP";

import CardManagement from "../card_settings/CardManagement";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import BankCardList from "./cards/BankCardList";
import CardHubSSO from "./cards/CardHubSSO";
import UserSettingsProfilePage from "./profile_settings/UserSettingsProfilePage";
import ExternalAccountManager from "./linked_accounts/ExternalAccountManager";
import AlertsContainer from "./alerts/AlertsContainer";
import Recipients from "./recipients/Recipients";
import SecuritySettingsContainer from "./security/SecuritySettingsContainer";
import {
  InstitutionSettingsContextProvider,
  useInstitutionSettings,
} from "../contexts/InstitutionSettingsContext";
import ReplaceACardContainer from "./cards/ReplaceACardContainer/ReplaceACardContainer";
import SettingsBanner from "./SettingsBanner";
import ScrollToTopOnRouteChange from "../ScrollToTopOnRouteChange";
import { AccountContextProvider } from "../contexts/AccountContext";
import BusinessBankingAPITokenList from "./business_banking_api/BusinessBankingAPITokenList";
import BusinessBankingAPITokenForm from "./business_banking_api/BusinessBankingAPITokenForm";
import MCPConnectorsCard from "./ai_connectors/MCPConnectorsCard";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import EmailChangeConfirmScreen from "../EmailChangeConfirm";
import PageLayout from "../../src/components/PageLayout";


interface SettingsProps {
  externalAccounts: Account[];
  accounts: Account[];
  uatMicrodepAmntOne: string;
  uatMicrodepAmntTwo: string;
  memberships: Membership[];
  devices: string;
  features: API.UserFeatures;
}

enum SettingsTabToRoute {
  "Profile" = "/profile",
  "Linked accounts" = "/linked_accounts",
  "Recipients" = "/recipients",
  "Security" = "/security",
  "Cards" = "/cards",
  "Alerts" = "/alerts",
  "API banking" = "/api_banking_token_list",
  "AI connectors" = "/ai_connectors",
}
export type SettingsTab = keyof typeof SettingsTabToRoute;

const convertRouteToRegex = (route: string) => {
  if (route === "/cards") {
    return /^\/cards(\/([0-9a-z-]+)(\/(travel_notice|limit_increase))?)?\/?$/;
  }

  if (route === "/alerts") {
    return /^\/alerts(\/([0-9a-z-]+)(\/(balance|spending|security|custom))?)?\/?$/;
  }

  if (route === "/api_banking_token_list") {
    return /^\/api_banking_token_list(\/(create|[0-9a-z-]+\/(edit|view)))?\/?$/;
  }

  return new RegExp(`^${route}/?$`);
};

export const findRouteIndexOfPathname = (
  pathname: string,
  settings: SettingsTab[],
) =>
  settings.findIndex((setting) =>
    convertRouteToRegex(SettingsTabToRoute[setting]).test(
      pathname.toLowerCase(),
    ),
  );

const SettingsTabs = () => {
  const { currentUser } = useCurrentUser();
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const institutionSettings = useInstitutionSettings();
  const showMcpTab =
    features.model_context_protocol_server &&
    institutionSettings.mcp_server_hostname;
  const settings: SettingsTab[] = [
    "Profile",
    ...(features.ach ||
    features.allow_external_account_linking_without_transferable_accounts
      ? (["Linked accounts"] as SettingsTab[])
      : []),
    ...(featureEquals(
      features as unknown as Features,
      "p2p",
      "m2m" as FeatureValue,
    ) ||
    features.bill_pay ||
    features.wires
      ? (["Recipients"] as SettingsTab[])
      : []),
    "Security",
    ...(features["integrations.base.Card_permission"] ? (["Cards"] as SettingsTab[]) : []),
    ...(features.enhanced_alerts ? (["Alerts"] as SettingsTab[]) : []),
    ...(features.business_banking_api && currentUser?.isBusiness()
      ? (["API banking"] as SettingsTab[])
      : []),
    ...(showMcpTab ? (["AI connectors"] as SettingsTab[]) : []),
  ];

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname) {
      const index = findRouteIndexOfPathname(location.pathname, settings);
      if (index !== -1) {
        setSelectedTabIndex(index);
      }
    }
  }, [location.pathname]);

  const onTabChange = (index: number) => {
    setSelectedTabIndex(index);
    navigate(SettingsTabToRoute[settings[index]]);
  };

  return (
    <PageLayout maxWidth="l">
      <SettingsBanner />
      <div className="margin--top--l">
        <Tabs selectedIndex={selectedTabIndex} onTabChange={onTabChange}>
          <Tabs.List>
            {settings.map((setting) => {
              const tabName = l10n.getString(
                {
                  Profile: "tab-settings-profile",
                  "Linked accounts": "tab-settings-linked-accounts",
                  Recipients: "tab-settings-recipients",
                  Security: "tab-settings-security",
                  Cards: "tab-settings-cards",
                  Alerts: "tab-settings-alerts",
                  "API banking": "tab-settings-api-banking",
                  "AI connectors": "tab-settings-ai-connectors",
                }[setting],
              );
              return <Tabs.Tab key={setting} label={tabName} tabId={setting} />;
            })}
          </Tabs.List>
        </Tabs>
        <Outlet />
      </div>
    </PageLayout>
  );
};

const SettingsRouter = ({
  externalAccounts,
  uatMicrodepAmntOne,
  uatMicrodepAmntTwo,
  accounts,
  devices,
  features,
  memberships,
}: SettingsProps) => {
  const institutionSettings = useInstitutionSettings();
  const isBusinessUser = entities.useIsBusinessUser();
  const showMcpTab =
    features.model_context_protocol_server &&
    institutionSettings.mcp_server_hostname;

  const hasCardPermission = !!(
    features.cards && features["integrations.base.Card_permission"]
  );
  const cardsUI = useMemo(() => {
    if (!hasCardPermission) {
      return null;
    }
    if (
      !isBusinessUser &&
      features.cardhub_sdk &&
      institutionSettings.card_processor === "cardhub"
    ) {
      // CardHub UI
      return <Route path="cards" element={<CardHubSSO />} />;
    }

    if (features.olb_enable_replace_a_card || features.olb_card_controls) {
      // Cards V2 UI
      return ["/cards", "/cards/:id/*"].map((path) => (
        <Route key={path} path={path} element={<BankCardList />} />
      ));
    }

    // Default Cards UI
    return <Route path="cards" element={<CardManagement />} />;
  }, [
    isBusinessUser,
    hasCardPermission,
    features.cardhub_sdk,
    features.olb_enable_replace_a_card,
    institutionSettings.card_processor,
    features.olb_card_controls,
  ]);

  return (
    <div className="settings-nav-bar-desktop">
      <Router basename="settings">
        <SudoProvider>
          <ScrollToTopOnRouteChange />
          <Routes>
            <Route path={`${SudoRoutes.RequestOTP}`} element={<RequestOTP />} />
            <Route path={`${SudoRoutes.SubmitOTP}`} element={<SubmitOTP />} />
            {hasCardPermission && (
              <Route
                path="cards/replace-a-card/:cardId/*"
                element={<ReplaceACardContainer />}
              />
            )}
            {features.business_banking_api && (
              <Route
                element={
                  <PageLayout maxWidth="l">
                    <Outlet />
                  </PageLayout>
                }
              >
                <Route
                  path="api_banking_token_list/create/*"
                  element={<BusinessBankingAPITokenForm mode="create" />}
                />
                <Route
                  path="api_banking_token_list/:tokenId/edit/*"
                  element={<BusinessBankingAPITokenForm mode="edit" />}
                />
                <Route
                  path="api_banking_token_list/:tokenId/view/*"
                  element={<BusinessBankingAPITokenForm mode="view" />}
                />
              </Route>
            )}
            <Route path="/" element={<SettingsTabs />}>
              {(features.ach ||
                features.allow_external_account_linking_without_transferable_accounts) && (
                <Route
                  path="linked_accounts/*"
                  element={
                    <ExternalAccountManager
                      externalAccounts={externalAccounts}
                      uatMicrodepAmntOne={uatMicrodepAmntOne}
                      uatMicrodepAmntTwo={uatMicrodepAmntTwo}
                    />
                  }
                />
              )}
              {(featureEquals(
                features as unknown as Features,
                "p2p",
                "m2m" as FeatureValue,
              ) ||
                features.bill_pay ||
                features.wires) && (
                <Route
                  path="recipients"
                  element={<Recipients accounts={accounts} />}
                />
              )}
              <Route
                path="security"
                element={
                  <SecuritySettingsContainer
                    accounts={accounts}
                    devices={devices}
                    features={features}
                  />
                }
              />
              <Route
                path="email-change-confirmation"
                element={<EmailChangeConfirmScreen />}
              />
              {cardsUI}
              {features.enhanced_alerts &&
                ["/alerts", "/alerts/*"].map((path) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <AccountContextProvider accounts={accounts}>
                        <AlertsContainer />
                      </AccountContextProvider>
                    }
                  />
                ))}
              {!features.enhanced_alerts && (
                <Route
                  path="/alerts"
                  element={<Navigate to="/security" replace />}
                />
              )}
              {features.business_banking_api && (
                <Route
                  path="api_banking_token_list/*"
                  element={<BusinessBankingAPITokenList />}
                />
              )}
              {showMcpTab && (
                <Route
                  path="ai_connectors"
                  element={
                    <MCPConnectorsCard
                      mcpServerHostname={
                        institutionSettings.mcp_server_hostname || ""
                      }
                      tokenLifespanDays={
                        institutionSettings.dcr_refresh_token_lifespan || 7
                      }
                      institutionName={institutionSettings.long_name || ""}
                    />
                  }
                />
              )}
              {["", "profile", "profile/*"].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <UserSettingsProfilePage memberships={memberships} />
                  }
                />
              ))}
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
          </Routes>
        </SudoProvider>
      </Router>
    </div>
  );
};

const SettingsNavBarContainer = ({
  externalAccounts,
  uatMicrodepAmntOne,
  uatMicrodepAmntTwo,
  accounts,
  devices,
  features,
  memberships,
}: SettingsProps) => (
  <InstitutionSettingsContextProvider>
    <SettingsRouter
      externalAccounts={externalAccounts}
      uatMicrodepAmntOne={uatMicrodepAmntOne}
      uatMicrodepAmntTwo={uatMicrodepAmntTwo}
      accounts={accounts}
      devices={devices}
      features={features}
      memberships={memberships}
    />
  </InstitutionSettingsContextProvider>
);

export default SettingsNavBarContainer;
